import type {
  GroupProDto,
  ProGroupListItemDto,
  UserProDto,
} from '@splitfiesta/shared';
import { PRO_FOREVER_SENTINEL, isProActive } from '@splitfiesta/shared';

import { EXAMPLE_GROUP_ID } from '../fixtures/exampleGroup';
import { queryKeys } from '../queries/queryKeys';

export { isProActive } from '@splitfiesta/shared';

const nowIso = () => new Date().toISOString();

export const ALREADY_PRO_BY_YOU_KEY = 'errors:pro.alreadyProByYou';

/** The 409 a retried add gets when the first POST already went through. */
export function isAlreadyProByYouError(error: unknown): boolean {
  return (
    (error as { translationKey?: unknown } | null)?.translationKey ===
    ALREADY_PRO_BY_YOU_KEY
  );
}

const PRO_GROUP_NOT_FOUND_KEY = 'errors:pro.groupNotFound';

/**
 * The 404 a retried remove gets when the first DELETE already went through:
 * the group is no longer one of the caller's, which is what was asked.
 */
export function isProGroupGoneError(error: unknown): boolean {
  return (
    (error as { translationKey?: unknown } | null)?.translationKey ===
    PRO_GROUP_NOT_FOUND_KEY
  );
}

/** The date part of "Pro until …" in the viewer's zone, or null for no end. */
export function formatProUntil(
  until: string | null,
  locale: string
): string | null {
  if (!until || until === PRO_FOREVER_SENTINEL) return null;
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(until));
}

/**
 * When a Pro slot can move again, in the viewer's zone. On the day itself
 * the time is added: "on October 12" read on October 12 before that hour
 * would contradict the refusal it explains.
 */
export function formatProMovableAt(
  movableAt: string | null,
  locale: string,
  now: string = nowIso()
): string | null {
  if (!movableAt || movableAt === PRO_FOREVER_SENTINEL) return null;
  const at = new Date(movableAt);
  if (Number.isNaN(at.getTime())) return null;
  const today = new Date(now);
  const sameDay =
    at.getFullYear() === today.getFullYear() &&
    at.getMonth() === today.getMonth() &&
    at.getDate() === today.getDate();
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(sameDay && { hour: 'numeric', minute: '2-digit' }),
  }).format(at);
}

/** A function, not a const, so tests that replace the queryKeys module can still import core. */
export function proInvalidateKeys() {
  return [
    queryKeys.groups(),
    queryKeys.allActivityFeeds(),
    queryKeys.proGroups(),
  ] as const;
}

/**
 * Absent = not Pro. A persisted cache can outlive `until`, so the client
 * re-checks it; an expired group has no active supporters left either.
 */
export function groupPro(
  group: { pro?: GroupProDto } | null | undefined,
  now: string = nowIso()
): GroupProDto {
  const pro = group?.pro;
  if (!pro) return { active: false, until: null, by: [] };
  const active = pro.active && isProActive(pro.until, now);
  return { active, until: pro.until, by: active ? pro.by : [] };
}

/** A null `until` means no end: the server verdict stands. */
export function userPro(
  user: { pro?: UserProDto } | null | undefined,
  now: string = nowIso()
): UserProDto {
  const pro = user?.pro;
  if (!pro) {
    return {
      active: false,
      until: null,
      source: 'none',
      slots: { used: 0, total: 0 },
      firstMoveAvailable: false,
      freeGroupExpenseLimit: 0,
    };
  }
  const active =
    pro.active && (pro.until === null || isProActive(pro.until, now));
  return { ...pro, active };
}

export interface GroupProEntry {
  /** The Pro sheet can open for this group at all. */
  available: boolean;
  /** The group is Pro: its pill opens the sheet. */
  isPro: boolean;
  /** A Pro viewer on a group that is not Pro: "Make Pro" opens the sheet. */
  canMakePro: boolean;
}

/** Which Pro entry a group screen (header, settings) shows the viewer. */
export function groupProEntry(
  {
    proEnabled,
    user,
    group,
  }: {
    proEnabled: boolean;
    user: { pro?: UserProDto } | null | undefined;
    group: { id: string; pro?: GroupProDto };
  },
  now: string = nowIso()
): GroupProEntry {
  // The example group is client-side: a Pro call for it would reach the real API.
  const available = proEnabled && group.id !== EXAMPLE_GROUP_ID;
  const isPro = available && groupPro(group, now).active;
  const canMakePro = available && !isPro && userPro(user, now).active;
  return { available, isPro, canMakePro };
}

/** True when the caller is an active holder; a lapsed holder is not in `by`. */
export function isAlreadyProByYou(
  group: { pro?: GroupProDto } | null | undefined,
  userId: string,
  now: string = nowIso()
): boolean {
  return groupPro(group, now).by.includes(userId);
}

/**
 * A group the Pro groups picker offers: any group the header would open the
 * Pro sheet for, except one this viewer already holds. A group someone else
 * made Pro stays, since it can take a backup.
 */
export function isProPickerEligible(
  input: {
    proEnabled: boolean;
    user: { id?: string; pro?: UserProDto } | null | undefined;
    group: { id: string; pro?: GroupProDto };
  },
  now: string = nowIso()
): boolean {
  return (
    groupProEntry(input, now).available &&
    !isAlreadyProByYou(input.group, input.user?.id ?? '', now)
  );
}

export type ProDialogVariant =
  | 'notPro'
  | 'alreadyProByYou'
  | 'add'
  | 'replaceFirstMove'
  | 'replaceCooldown'
  | 'slotsExhausted';

export interface ProDialogInput {
  userPro: UserProDto;
  groupPro: GroupProDto;
  userId: string;
  groupId: string;
  /** The caller's held Pro groups from `GET /pro/groups`. */
  proGroups: readonly ProGroupListItemDto[];
  now?: string;
}

/**
 * Which Pro dialog/sheet to show. A lapsed holder is missing from `by` but can
 * still remove, so holding also comes from `proGroups` and is checked before
 * the caller's own Pro state.
 */
export function proDialogVariant(input: ProDialogInput): ProDialogVariant {
  const now = input.now ?? nowIso();
  const holds =
    input.proGroups.some(item => item.groupId === input.groupId) ||
    isAlreadyProByYou({ pro: input.groupPro }, input.userId, now);
  if (holds) {
    return 'alreadyProByYou';
  }
  const caller = userPro({ pro: input.userPro }, now);
  if (!caller.active) return 'notPro';
  if (caller.slots.used < caller.slots.total) return 'add';
  if (caller.firstMoveAvailable) return 'replaceFirstMove';
  return input.proGroups.some(item => item.movableAt <= now)
    ? 'replaceCooldown'
    : 'slotsExhausted';
}

/** ISO strings compare lexically, so no Date parsing is needed. */
export function isProSlotLocked(
  movableAt: string,
  now: string = nowIso()
): boolean {
  return movableAt > now;
}
